import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  MessageSquare,
  Send,
  Search,
  Users,
  User,
  Smile,
  Paperclip,
  MoreVertical,
  Check,
  CheckCheck,
  Clock,
  X
} from 'lucide-react';
import { useSocket } from '../hooks/useSocket';
import { selectUser } from '../store/slices/authSlice';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface Message {
  _id: string;
  content: string;
  sender: {
    _id: string;
    name: string;
    email: string;
  };
  conversationId: string;
  createdAt: string;
  readBy?: string[];
  emoji?: string;
}

interface Conversation {
  _id: string;
  type: 'group' | 'private';
  name?: string;
  participants: Array<{
    _id: string;
    name: string;
    email: string;
  }>;
  lastMessage?: Message;
  unreadCount?: number;
  createdAt: string;
}

const EMOJI_CATEGORIES = {
  'Smileys & People': ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓'],
  'Gestures': ['👋', '🤚', '🖐', '✋', '🖖', '👌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏'],
  'Objects': ['💻', '📱', '⌚', '📷', '📹', '🎥', '📽️', '🎬', '📺', '📻', '🎙️', '🎚️', '🎛️', '⏱️', '⏲️', '⏰', '🕰️', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯️', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '💰', '💳', '💎', '⚖️', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🔩', '⚙️', '🧰', '🧲', '🧪', '🧫', '🧬', '🔬', '🔭', '📡'],
  'Symbols': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳', '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️', '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️', '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', '♨️', '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❓', '❕', '❔', '‼️', '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️', '🔰', '♻️', '✅', '🈯', '💹', '❇️', '✳️', '❎', '🌐', '💠', 'Ⓜ️', '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🈳', '🈂️', '🛂', '🛃', '🛄', '🛅', '🚹', '🚺', '🚼', '🚻', '🚮', '🎦', '📶', '🈁', '🔣', 'ℹ️', '🔤', '🔡', '🔠', '🔢', '🔟', '▶️', '⏸️', '⏯️', '⏹️', '⏺️', '⏭️', '⏮️', '⏩', '⏪', '⏫', '⏬', '◀️', '🔼', '🔽', '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️', '↪️', '↩️', '⤴️', '⤵️', '🔀', '🔁', '🔂', '🔄', '🔃', '🎵', '🎶', '➕', '➖', '➗', '✖️', '💲', '💱', '™️', '©️', '®️', '〰️', '➰', '➿', '🔚', '🔙', '🔛', '🔝', '🔜', '✔️', '☑️', '🔘', '⚪', '⚫', '🔴', '🔵', '🔺', '🔻', '🔸', '🔹', '🔶', '🔷', '🔳', '🔲', '▪️', '▫️', '◾', '◽', '◼️', '◻️', '🟥', '🟧', '🟨', '🟩', '🟦', '🟪', '🟫', '⬛', '⬜', '🔈', '🔇', '🔉', '🔊', '🔔', '🔕', '📣', '📢', '👁‍🗨', '💬', '💭', '🗯️', '♠️', '♣️', '♥️', '♦️', '🃏', '🎴', '🀄', '🕐', '🕑', '🕒', '🕓', '🕔', '🕕', '🕖', '🕗', '🕘', '🕙', '🕚', '🕛', '🕜', '🕝', '🕞', '🕟', '🕠', '🕡', '🕢', '🕣', '🕤', '🕥', '🕦', '🕧']
};

const MessagesPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const user = useSelector(selectUser);
  const token = localStorage.getItem('token');
  const { socket, isConnected } = useSocket(token);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [conversationType, setConversationType] = useState<'all' | 'group' | 'private'>('all');
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [typingUsers, setTypingUsers] = useState<Record<string, string[]>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  // Charger les membres du projet
  useEffect(() => {
    const fetchProjectMembers = async () => {
      try {
        const response = await axios.get(`${API_BASE}/projects/${projectId}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjectMembers(response.data.data || []);
      } catch (error) {
        console.error('Error fetching project members:', error);
      }
    };

    if (projectId) {
      fetchProjectMembers();
    }
  }, [projectId, token]);

  // Charger les conversations
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE}/projects/${projectId}/conversations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setConversations(response.data.data || []);
      } catch (error) {
        console.error('Error fetching conversations:', error);
        setConversations([]);
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchConversations();
    }
  }, [projectId, token]);

  // Charger les messages d'une conversation
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedConversation) {
        setMessages([]);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE}/conversations/${selectedConversation._id}/messages`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setMessages(response.data.data || []);
        scrollToBottom();
      } catch (error) {
        console.error('Error fetching messages:', error);
        setMessages([]);
      }
    };

    fetchMessages();
  }, [selectedConversation, token]);

  // Rejoindre/quitter les conversations via Socket.io
  useEffect(() => {
    if (!socket || !selectedConversation) return;

    // Rejoindre la conversation
    (socket as any).emit('join-conversation', { conversationId: selectedConversation._id });

    const handleNewMessage = (message: any) => {
      // Normaliser le format du message
      const normalizedMessage: Message = {
        _id: message._id || message.id,
        content: message.content,
        sender: message.sender || { _id: message.senderId, name: 'Utilisateur', email: '' },
        conversationId: message.conversationId?.toString() || message.conversation,
        createdAt: message.createdAt || message.timestamp || new Date().toISOString(),
        readBy: message.readBy || [],
        emoji: message.emoji
      };

      if (normalizedMessage.conversationId?.toString() === selectedConversation._id.toString()) {
        setMessages(prev => {
          // Éviter les doublons
          if (prev.some(m => m._id === normalizedMessage._id)) {
            return prev;
          }
          return [...prev, normalizedMessage];
        });
        scrollToBottom();
      }
      // Mettre à jour la liste des conversations
      updateConversationLastMessage(normalizedMessage);
    };

    (socket as any).on('newMessage', handleNewMessage);
    (socket as any).on('messageRead', handleMessageRead);
    
    const handleUserTyping = (data: { conversationId: string; userId: string; userName: string }) => {
      if (data.conversationId === selectedConversation._id) {
        setTypingUsers(prev => ({
          ...prev,
          [data.conversationId]: [...(prev[data.conversationId] || []).filter(id => id !== data.userId), data.userId]
        }));
      }
    };

    const handleUserStoppedTyping = (data: { conversationId: string; userId: string }) => {
      if (data.conversationId === selectedConversation._id) {
        setTypingUsers(prev => ({
          ...prev,
          [data.conversationId]: (prev[data.conversationId] || []).filter(id => id !== data.userId)
        }));
      }
    };

    (socket as any).on('userTyping', handleUserTyping);
    (socket as any).on('userStoppedTyping', handleUserStoppedTyping);

    // Timer pour arrêter l'indicateur de frappe après 3 secondes
    const typingTimeout = setInterval(() => {
      setTypingUsers(prev => ({
        ...prev,
        [selectedConversation._id]: []
      }));
    }, 3000);

    return () => {
      if (socket) {
        (socket as any).emit('leave-conversation', { conversationId: selectedConversation._id });
        (socket as any).off('newMessage', handleNewMessage);
        (socket as any).off('messageRead', handleMessageRead);
        (socket as any).off('userTyping', handleUserTyping);
        (socket as any).off('userStoppedTyping', handleUserStoppedTyping);
      }
      clearInterval(typingTimeout);
    };
  }, [socket, selectedConversation]);

  const handleMessageRead = (data: { conversationId: string; userId: string; messageId?: string }) => {
    if (data.conversationId === selectedConversation?._id) {
      setMessages(prev =>
        prev.map(msg => {
          if (data.messageId && msg._id === data.messageId) {
            return { ...msg, readBy: [...(msg.readBy || []), data.userId] };
          }
          if (!data.messageId && !msg.readBy?.includes(data.userId)) {
            return { ...msg, readBy: [...(msg.readBy || []), data.userId] };
          }
          return msg;
        })
      );
    }
  };

  const updateConversationLastMessage = (message: Message) => {
    setConversations(prev =>
      prev.map(conv => {
        if (conv._id.toString() === message.conversationId?.toString()) {
          const isOwnMessage = message.sender._id === user?._id;
          return {
            ...conv,
            lastMessage: message,
            unreadCount: isOwnMessage ? conv.unreadCount : (conv.unreadCount || 0) + 1
          };
        }
        return conv;
      })
    );
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Envoyer un message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !socket) return;

    try {
      const response = await axios.post(
        `${API_BASE}/conversations/${selectedConversation._id}/messages`,
        {
          content: newMessage.trim(),
          emoji: null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const message = response.data.data;
      // Ne pas ajouter le message ici car il sera reçu via Socket.io
      setNewMessage('');
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Créer une nouvelle conversation privée
  const createPrivateConversation = async (memberId: string) => {
    try {
      const response = await axios.post(
        `${API_BASE}/projects/${projectId}/conversations`,
        {
          type: 'private',
          participants: [memberId]
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const newConversation = response.data.data;
      setConversations(prev => [newConversation, ...prev]);
      setSelectedConversation(newConversation);
    } catch (error: any) {
      if (error.response?.status === 409) {
        // Conversation existe déjà
        const existingConv = conversations.find(
          conv => conv.type === 'private' && conv.participants.some(p => p._id === memberId)
        );
        if (existingConv) {
          setSelectedConversation(existingConv);
        }
      } else {
        console.error('Error creating conversation:', error);
      }
    }
  };

  // Créer une conversation de groupe
  const createGroupConversation = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) return;

    try {
      const response = await axios.post(
        `${API_BASE}/projects/${projectId}/conversations`,
        {
          type: 'group',
          name: groupName.trim(),
          participants: selectedMembers
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const newConversation = response.data.data;
      setConversations(prev => [newConversation, ...prev]);
      setSelectedConversation(newConversation);
      setShowCreateGroupModal(false);
      setGroupName('');
      setSelectedMembers([]);
    } catch (error) {
      console.error('Error creating group conversation:', error);
    }
  };

  const toggleMemberSelection = (memberId: string) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Ajouter un emoji au message
  const insertEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  // Filtrer les conversations
  const filteredConversations = conversations.filter(conv => {
    if (conversationType === 'group' && conv.type !== 'group') return false;
    if (conversationType === 'private' && conv.type !== 'private') return false;
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const nameMatch = conv.name?.toLowerCase().includes(searchLower);
      const participantMatch = conv.participants.some(p =>
        p.name.toLowerCase().includes(searchLower) || p.email.toLowerCase().includes(searchLower)
      );
      return nameMatch || participantMatch;
    }
    return true;
  });

  // Formater la date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Hier';
    } else if (days < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  };

  // Obtenir le nom de la conversation
  const getConversationName = (conv: Conversation) => {
    if (conv.type === 'group' && conv.name) {
      return conv.name;
    }
    if (conv.type === 'private') {
      const otherParticipant = conv.participants.find(p => p._id !== user?._id);
      return otherParticipant?.name || 'Utilisateur';
    }
    return 'Conversation';
  };

  // Générer une couleur pour un utilisateur (basée sur son ID)
  const getUserColor = (userId: string) => {
    const colors = [
      'bg-pink-100 text-pink-600',
      'bg-purple-100 text-purple-600',
      'bg-blue-100 text-blue-600',
      'bg-green-100 text-green-600',
      'bg-yellow-100 text-yellow-600',
      'bg-orange-100 text-orange-600',
      'bg-indigo-100 text-indigo-600',
      'bg-red-100 text-red-600',
    ];
    const index = parseInt(userId.slice(-1), 16) % colors.length;
    return colors[index];
  };

  // Obtenir l'avatar de la conversation
  const getConversationAvatar = (conv: Conversation, size: 'sm' | 'md' = 'md') => {
    if (conv.type === 'group') {
      return <Users className={size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} />;
    }
    const otherParticipant = conv.participants.find(p => p._id !== user?._id);
    const initials = otherParticipant?.name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2) || '?';
    return <span className={`${size === 'sm' ? 'text-[10px]' : 'text-xs'} font-semibold`}>{initials}</span>;
  };

  // Rôles des membres — inclus dans la réponse /members (projectRole), pas de calls séparés
  const memberRoles = useMemo(() => {
    const roles: Record<string, string> = {};
    for (const member of projectMembers) {
      roles[member.user._id] =
        (member as any).projectRole || (member.role === 'creator' ? 'Creator' : 'Member');
    }
    return roles;
  }, [projectMembers]);

  const getMemberRoleSync = (memberId: string): string => {
    return memberRoles[memberId] || 'Member';
  };

  // Fermer le sélecteur d'emojis en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-200px)] bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Sidebar - Liste des conversations */}
      <div className="w-80 border-r border-gray-200 flex flex-col bg-white">
        {/* Header de la sidebar */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Messages</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCreateGroupModal(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Nouveau groupe"
              >
                <Users className="h-5 w-5 text-gray-600" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <MoreVertical className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Barre de recherche */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filtres */}
          <div className="flex gap-2">
            <button
              onClick={() => setConversationType('all')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                conversationType === 'all'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tous
            </button>
            <button
              onClick={() => setConversationType('group')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                conversationType === 'group'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Groupes
            </button>
            <button
              onClick={() => setConversationType('private')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                conversationType === 'private'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Private
            </button>
          </div>
        </div>

        {/* Liste des conversations */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              {searchQuery ? 'No conversation found' : 'No conversations'}
            </div>
          ) : (
            filteredConversations.map(conv => {
              const isSelected = selectedConversation?._id === conv._id;
              const otherParticipant = conv.type === 'private' 
                ? conv.participants.find(p => p._id !== user?._id)
                : null;
              const avatarColor = otherParticipant 
                ? getUserColor(otherParticipant._id)
                : 'bg-blue-100 text-blue-600';

              return (
                <div
                  key={conv._id}
                  onClick={() => setSelectedConversation(conv)}
                  className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full ${conv.type === 'group' ? 'bg-blue-100 text-blue-600' : avatarColor} flex items-center justify-center flex-shrink-0`}>
                      {getConversationAvatar(conv)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <h3 className="font-semibold text-gray-900 truncate text-sm">
                          {getConversationName(conv)}
                        </h3>
                        {conv.unreadCount && conv.unreadCount > 0 && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold bg-blue-500 text-white rounded-full min-w-[18px] text-center flex-shrink-0">
                            {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                          </span>
                        )}
                      </div>
                      {conv.type === 'group' ? (
                        <p className="text-xs text-gray-500 mb-1">Groupe de discussion</p>
                      ) : otherParticipant ? (
                        <p className="text-xs text-gray-500 mb-1">{getMemberRoleSync(otherParticipant._id)}</p>
                      ) : null}
                      {conv.lastMessage && (
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-600 truncate flex-1">
                            {conv.lastMessage.content}
                          </p>
                          <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                            {formatDate(conv.lastMessage.createdAt)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Liste des membres pour créer des conversations privées */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <h3 className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">Membres du projet</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {projectMembers
              .filter(member => member.user._id !== user?._id)
              .slice(0, 5)
              .map(member => {
                const initials = member.user.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase().substring(0, 2) || '?';
                const avatarColor = getUserColor(member.user._id);
                
                return (
                  <button
                    key={member.user._id}
                    onClick={() => createPrivateConversation(member.user._id)}
                    className="w-full flex items-center gap-2 p-2 hover:bg-white rounded-lg transition-colors text-left"
                  >
                    <div className={`w-8 h-8 rounded-full ${avatarColor} flex items-center justify-center text-[10px] font-semibold flex-shrink-0`}>
                      {initials}
                    </div>
                    <span className="text-sm text-gray-700 truncate">{member.user.name}</span>
                  </button>
                );
              })}
            {projectMembers.filter(member => member.user._id !== user?._id).length > 5 && (
              <button className="w-full text-xs text-blue-600 hover:text-blue-700 font-medium text-left p-2">
                +{projectMembers.filter(member => member.user._id !== user?._id).length - 5} autres membres
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Zone principale - Messages */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Header de la conversation */}
            <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedConversation.type === 'private' ? (() => {
                  const otherParticipant = selectedConversation.participants.find(p => p._id !== user?._id);
                  const avatarColor = otherParticipant ? getUserColor(otherParticipant._id) : 'bg-blue-100 text-blue-600';
                  return (
                    <div className={`w-10 h-10 rounded-full ${avatarColor} flex items-center justify-center flex-shrink-0`}>
                      {getConversationAvatar(selectedConversation)}
                    </div>
                  );
                })() : (
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                    {getConversationAvatar(selectedConversation)}
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900 text-base">
                    {getConversationName(selectedConversation)}
                  </h3>
                  {selectedConversation.type === 'group' && (
                    <p className="text-xs text-gray-500">
                      {selectedConversation.participants.length} participants
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <MoreVertical className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Zone des messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun message</p>
                  </div>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isOwn = msg.sender._id === user?._id;
                  const prevMsg = index > 0 ? messages[index - 1] : null;
                  const showAvatar = !prevMsg || prevMsg.sender._id !== msg.sender._id;
                  const showTime = !prevMsg || 
                    new Date(msg.createdAt).getTime() - new Date(prevMsg.createdAt).getTime() > 300000; // 5 minutes

                  const senderInitials = msg.sender.name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || '?';
                  const senderAvatarColor = getUserColor(msg.sender._id);

                  return (
                    <div key={msg._id} className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''} ${showAvatar ? 'mt-4' : 'mt-1'}`}>
                      {showAvatar && !isOwn && (
                        <div className={`w-8 h-8 rounded-full ${senderAvatarColor} flex items-center justify-center text-[10px] font-semibold flex-shrink-0`}>
                          {senderInitials}
                        </div>
                      )}
                      {showAvatar && isOwn && <div className="w-8 flex-shrink-0" />}
                      <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-[75%]`}>
                        {showAvatar && !isOwn && (
                          <span className="text-xs font-medium text-gray-700 mb-1.5">{msg.sender.name}</span>
                        )}
                        <div
                          className={`rounded-2xl px-4 py-2.5 shadow-sm ${
                            isOwn
                              ? 'bg-blue-500 text-white'
                              : 'bg-white border border-gray-200 text-gray-900'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
                        </div>
                        {showTime && (
                          <div className={`flex items-center gap-1.5 mt-1.5 ${isOwn ? 'flex-row-reverse' : ''}`}>
                            {isOwn && (
                              <span className="flex items-center gap-1">
                                {msg.readBy && msg.readBy.length > 1 ? (
                                  <CheckCheck className="h-3.5 w-3.5 text-blue-500" />
                                ) : (
                                  <Check className="h-3.5 w-3.5 text-gray-400" />
                                )}
                              </span>
                            )}
                            <span className="text-[11px] text-gray-400">
                              {formatDate(msg.createdAt)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              
              {/* Indicateur de frappe */}
              {typingUsers[selectedConversation._id] && typingUsers[selectedConversation._id].length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 italic">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span>
                    {typingUsers[selectedConversation._id].length === 1
                      ? 'Typing...'
                      : `${typingUsers[selectedConversation._id].length} people typing...`}
                  </span>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Zone de saisie */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <div className="relative">
                    <input
                      ref={inputRef}
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder="Tapez un message..."
                      className="w-full px-4 py-2 pr-20 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                      <button
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        type="button"
                      >
                        <Smile className="h-5 w-5 text-gray-500" />
                      </button>
                      <button
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        type="button"
                      >
                        <Paperclip className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>
                  </div>

                  {/* Sélecteur d'emojis */}
                  {showEmojiPicker && (
                    <div
                      ref={emojiPickerRef}
                      className="absolute bottom-full right-0 mb-2 w-80 h-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden"
                    >
                      <div className="h-full overflow-y-auto p-3">
                        {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                          <div key={category} className="mb-4">
                            <h4 className="text-xs font-semibold text-gray-600 mb-2 sticky top-0 bg-white py-1">
                              {category}
                            </h4>
                            <div className="grid grid-cols-8 gap-1">
                              {emojis.map((emoji, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => insertEmoji(emoji)}
                                  className="p-2 hover:bg-gray-100 rounded text-lg transition-colors"
                                  type="button"
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="p-3 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-full transition-colors flex-shrink-0"
                  type="button"
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Select a conversation</p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de création de groupe */}
      {showCreateGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create a group</h3>
              <button
                onClick={() => {
                  setShowCreateGroupModal(false);
                  setGroupName('');
                  setSelectedMembers([]);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du groupe
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="E.g.: Marketing Team"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select members
              </label>
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2">
                {projectMembers
                  .filter(member => member.user._id !== user?._id)
                  .map(member => (
                    <button
                      key={member.user._id}
                      onClick={() => toggleMemberSelection(member.user._id)}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors text-left ${
                        selectedMembers.includes(member.user._id)
                          ? 'bg-blue-50 border border-blue-200'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
                        {member.user.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase().substring(0, 2) || '?'}
                      </div>
                      <span className="flex-1 text-sm text-gray-700">{member.user.name}</span>
                      {selectedMembers.includes(member.user._id) && (
                        <Check className="h-5 w-5 text-blue-500" />
                      )}
                    </button>
                  ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCreateGroupModal(false);
                  setGroupName('');
                  setSelectedMembers([]);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createGroupConversation}
                disabled={!groupName.trim() || selectedMembers.length === 0}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
